<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" :items="items" class="mc-page-three" />
      <p class="mb-4">
        {{ $t('setting.list.managementAsset.subdomain.subtitle') }}
      </p>
      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end">
          <b-button
            variant="outline-primary"
            class="ms-3"
            @click="$router.push({ name: 'Management Asset' })"
          >
            <i class="mc-fa icon-outline_arrow-left"></i>
            {{ $t('global.back') }}
          </b-button>
        </div>
      </div>
    </div>

    <div class="mc-form">
      <b-card>
        <b-row>
          <!-- Table -->
          <div class="table-responsive my-3 border border-1">
            <b-table
              small
              show-empty
              :items="subDomains"
              :fields="visibleFields"
              class="datatables"
              responsive="lg"
              empty-table
              :empty-text="$t('global.emptyTable')"
              :per-page="perPage"
              :current-page="currentPage"
              :busy="$apollo.queries.subDomains.loading"
              sort-by="companyName"
              @sort-changed="sortChanged"
            >
              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner class="align-middle" />
                </div>
              </template>

              <template #head()="data">
                {{ data.label }}
              </template>

              <template #cell(action)="data">
                <McButton
                  variant="primary"
                  :text="$t('global.edit')"
                  icon="icon-outline_edit"
                  @click="modalEdit(data.item)"
                />
                <b-button variant="primary" class="ms-2" @click="redirectToSubDomain(data.item)">
                  <i class="mc-fa fa-lg icon-outline_external-link align-middle"></i>
                  {{ $t('login.text.login') }}
                </b-button>
              </template>
            </b-table>
          </div>

          <div class="row">
            <div class="col-sm-12 col-md-6">
              <div id="tickets-table_length" class="dataTables_length">
                <div v-if="totalRows === 0">
                  <label class="d-inline-flex align-items-center">
                    {{ $t('global.text.displays') }}
                    <b-form-select
                      v-model="perPage"
                      class="form-select form-select-sm"
                      size="sm"
                      :options="pageOptions"
                      @input="fetchSubDomains"
                    />
                    {{ $t('global.text.entries') }}
                  </label>
                </div>

                <div v-else>
                  <label class="d-inline-flex align-items-center">
                    {{ $t('global.text.displays') }}
                    <b-form-select
                      v-model="perPage"
                      class="form-select form-select-sm"
                      size="sm"
                      :options="pageOptions"
                      @input="fetchSubDomains"
                    />
                    {{ $t('global.text.data') }}
                    <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong>
                    {{ $t('global.object.of') }} <strong>{{ totalRows }}</strong>
                    {{ $t('global.text.data') }}
                  </label>
                </div>
              </div>
            </div>
            <div class="col-sm-12 col-md-6">
              <div class="dataTables_paginate paging_simple_numbers float-end">
                <ul class="pagination pagination-rounded mb-0">
                  <!-- pagination -->
                  <b-pagination
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    @change="fetchSubDomains"
                  />
                </ul>
              </div>
            </div>
          </div>
        </b-row>
      </b-card>
    </div>

    <ModalEdit :companyId="companyId" @update-data="handleUpdateData" />
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import McButton from '@/components/McButton'
import ModalEdit from './components/subDomain/modalEdit'
import { mapGetters } from 'vuex'
import { Helper } from '@/mixin/helper'
import { GET_SUB_DOMAINS } from '@/services/api/setting/personalization/domainManagement'
import { GENERATE_TEMPORARY_ACCESS } from '@/services/api/auth'
import moment from 'moment'
moment.locale('id')
import { Cookie } from '@/mixin/local-cookie'

export default {
  page: {
    title: 'Manajemen Asset',
    meta: [{ name: 'description', content: appConfig.description }],
  },

  components: {
    Layout,
    PageHeader,
    McButton,
    ModalEdit,
  },

  mixins: [Helper],
  apollo: {
    subDomains: {
      query: GET_SUB_DOMAINS,
      variables: {
        filter: {
          pagination: {
            page: 1,
            show: 10,
          },
          orderBy: [JSON.stringify({ name: 'asc' })],
        },
      },
      manual: true,
      fetchPolicy: 'no-cache',
      result({ data, loading }) {
        if (!loading) {
          this.subDomains = data.domainManagements.domainManagements.data
          this.totalRows = data.domainManagements.domainManagements.pagination.total
        }
      },
    },
  },

  data() {
    return {
      subDomains: [],
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      pageOptions: [10, 25, 50, 100],
      orderBy: null,
      lastSortBy: { name: 'asc' },
      DEFAULT_EXPIRY_HOUR: 8,
      companyId: null,
    }
  },

  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
    }),
    title() {
      return this.$t('setting.list.managementAsset.subdomain.title')
    },
    visibleFields() {
      return [
        {
          key: 'companyName',
          column: 'name',
          label: this.$t('setting.list.managementAsset.subdomain.companyName'),
          sortable: true,
          tdClass: 'align-middle',
        },
        {
          key: 'vehicleTotal',
          column: 'vehicleTotal',
          label: this.$t('setting.list.managementAsset.subdomain.fleetTotal'),
          sortable: false,
          tdClass: 'align-middle',
        },
        {
          key: 'driverTotal',
          column: 'driverTotal',
          label: this.$t('setting.list.managementAsset.subdomain.driverTotal'),
          sortable: false,
          tdClass: 'align-middle',
        },
        {
          key: 'domainName',
          column: 'domainName',
          label: this.$t('setting.list.managementAsset.subdomain.domain'),
          sortable: true,
          tdClass: 'align-middle',
        },
        {
          key: 'action',
          label: this.$t('setting.list.managementAsset.subdomain.action'),
          sortable: false,
          tdClass: ['align-middle', 'text-center'],
          thClass: 'text-center',
        },
      ]
    },
    startData() {
      return this.subDomains.length > 0 ? (this.currentPage - 1) * this.perPage + 1 : 0
    },
    endData() {
      return Math.min(this.subDomains.length, this.currentPage * this.perPage)
    },
    items() {
      return [
        {
          text: this.$t('setting.homePage.pengaturan'),
          to: { name: 'Setting' },
        },
        {
          text: this.$t('setting.homePage.asset'),
          to: { name: 'Management Asset' },
        },
        {
          text: this.$t('setting.list.managementAsset.subdomain.title'),
          active: true,
        },
      ]
    },
  },

  methods: {
    async fetchSubDomains() {
      await this.$apollo.queries.subDomains
        .refetch({
          filter: {
            pagination: {
              page: this.currentPage,
              show: this.perPage,
            },
            orderBy: [JSON.stringify(this.orderBy)],
          },
        })
        .then((data) => {
          this.subDomains = data.data.domainManagements.domainManagements.data
          this.totalRows = data.data.domainManagements.domainManagements.pagination.total
        })
    },
    sortChanged(orderBy) {
      const sortColumn = this.visibleFields.find((o) => o.key === orderBy['sortBy']).column
      const sortType = orderBy.sortDesc ? 'desc' : 'asc'
      this.orderBy = { [sortColumn]: sortType }
      this.lastSortBy = { [sortColumn]: sortType }
      this.fetchSubDomains()
    },
    async redirectToSubDomain(company) {
      const input = {
        companyId: company.companyId,
        expiresOn: this.DEFAULT_EXPIRY_HOUR,
      }
      this.$apollo
        .mutate({
          mutation: GENERATE_TEMPORARY_ACCESS,
          variables: {
            input: input,
          },
        })
        .then((response) => {
          if (response.data.generateTemporaryAccess.isSuccessful == false) {
            return this.$mcToast.error(this.$t('setting.list.managementAsset.subdomain.failRedirect'))
          }
          const { url } = response.data.generateTemporaryAccess.generateTemporaryAccess
          const [subDomainURL, subDomainResource] = url.split('/authenticate/')

          if (!subDomainURL || !subDomainResource) {
            return this.$mcToast.error(this.$t('setting.list.managementAsset.subdomain.failRedirect'))
          }

          localStorage.removeItem('token')
          localStorage.removeItem('wsToken')
          localStorage.removeItem('domainName')
          localStorage.removeItem('domainNameExpired')
          localStorage.removeItem('payload')

          const expiryTime = moment().add(this.DEFAULT_EXPIRY_HOUR, 'hours').toDate().toString()
          Cookie.setLocalCookie('domain', Cookie.getSubdomain(subDomainURL), expiryTime, '/')
          window.open(`${subDomainURL}/?isLoginSuccess=true`, '_self')
        })
    },
    modalEdit(data) {
      this.companyId = data.companyId
      this.$bvModal.show('modal-edit')
    },
    handleUpdateData() {
      this.orderBy = this.lastSortBy
      this.fetchSubDomains()
    },
  },
}
</script>
